import React, { useState } from "react";
import { SettingsOutlined } from "@material-ui/icons";
import { Button, Switch } from "@my-scoot/component-library-legacy";
import { Field, Form } from "react-final-form";
import ExlyRadioGroup from "common/form/ExlyRadioGroup/Index";
import { ExlyModal } from "features/Common/modules/ExlyModal/ExlyModal";
import LearnMoreCta from "features/Common/modules/LearnMore/modules/LearnMoreCta/LearnMoreCta";
import { useOfferings } from "hooks/useOfferings";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { useOfferingVariationListStore } from "./store/useOfferingVariationList.store";
import {
  DISPLAY_STYLE_OPTIONS,
  OFFERING_VARIANTS_DISPLAY_STYLE,
  OFFERING_VARIATIONS_LEARN_MORE_HREFS,
  VARIATION_SETTINGS_FORM_KEYS,
} from "./OfferingVariationsList.constants";

export { OFFERING_VARIANTS_DISPLAY_STYLE };

const FORM_KEYS = VARIATION_SETTINGS_FORM_KEYS;

export const OfferingVariationsHeaderActions = ({
  showCreateButton = false,
  onCreate,
  createDisabled,
  createLabel,
}) => {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useNotifications();
  const { updateOffering, fetchOfferingDetails } = useOfferings();
  const offeringDetails = useOfferingVariationListStore(
    (state) => state.offeringDetails
  );
  const setOfferingDetails = useOfferingVariationListStore(
    (state) => state.setOfferingDetails
  );

  const displayStyle =
    offeringDetails?.metadata?.offering_variants_display_style ??
    OFFERING_VARIANTS_DISPLAY_STYLE.OPTIONS;
  const preselectDefault =
    offeringDetails?.metadata?.offering_variants_preselect_default ?? false;

  const openSettings = () => {
    setIsSettingsOpen(true);
  };

  const closeSettings = () => {
    if (isSaving) return;
    setIsSettingsOpen(false);
  };

  const handleSave = (values) => {
    if (isSaving) return;

    const selectedDisplayStyle =
      values?.[FORM_KEYS.DISPLAY_STYLE] ?? displayStyle;
    const selectedPreselectDefault = Boolean(
      values?.[FORM_KEYS.PRESELECT_DEFAULT]
    );

    if (!offeringDetails?.uuid) {
      setIsSettingsOpen(false);
      return;
    }

    if (
      selectedDisplayStyle === displayStyle &&
      selectedPreselectDefault === preselectDefault
    ) {
      setIsSettingsOpen(false);
      return;
    }

    // Backend merges allowlisted metadata keys; partial payload is intentional.
    updateOffering({
      offeringUuid: offeringDetails.uuid,
      updatedValues: {
        metadata: {
          offering_variants_display_style: selectedDisplayStyle,
          offering_variants_preselect_default: selectedPreselectDefault,
        },
      },
      onStart: () => setIsSaving(true),
      onEnd: () => setIsSaving(false),
      onSuccess: async (response) => {
        const fresh = await fetchOfferingDetails({
          offeringUuid: offeringDetails.uuid,
          forceFetch: true,
        });
        setOfferingDetails({
          ...(fresh || offeringDetails),
          metadata: {
            ...((fresh || offeringDetails).metadata || {}),
            offering_variants_display_style: selectedDisplayStyle,
            offering_variants_preselect_default: selectedPreselectDefault,
          },
        });
        notify(
          response?.message || "Settings saved successfully",
          notification_color_keys.success
        );
        setIsSettingsOpen(false);
      },
      onError: () => {
        notify(
          "Failed to save settings. Please try again.",
          notification_color_keys.error
        );
      },
    });
  };

  return (
    <div className="d-flex tw-items-center tw-gap-3">
      <LearnMoreCta
        href={OFFERING_VARIATIONS_LEARN_MORE_HREFS.OFFERING_VARTIATION}
      />
      <Button
        color="primary"
        size="medium"
        onClick={openSettings}
        buttonWrapperClassName="tw-flex tw-items-center tw-text-white"
      >
        <SettingsOutlined className="tw-w-[18px] tw-h-[18px] tw-mr-1" />
        Settings
      </Button>
      {isSettingsOpen && (
        <Form
          onSubmit={handleSave}
          initialValues={{
            [FORM_KEYS.DISPLAY_STYLE]: displayStyle,
            [FORM_KEYS.PRESELECT_DEFAULT]: preselectDefault,
          }}
        >
          {({ handleSubmit, values, form }) => (
            <ExlyModal
              title="Offering Variations Settings"
              open={isSettingsOpen}
              onClose={closeSettings}
              modal_props={{
                footerClassName: "!tw-justify-between",
                secondaryBtnClassName: "!tw-text-[#493ab1] !tw-min-w-[unset]",
                modalPaperClassName: "tw-min-w-[500px]",
              }}
              primaryBtnText="Save details"
              onPrimaryBtnClick={handleSubmit}
              primaryBtnProps={{ disabled: isSaving, loading: isSaving }}
              showSecondaryBtn
              onSecondaryBtnClick={closeSettings}
              secondaryBtnProps={{ disabled: isSaving }}
              className="!tw-p-0"
            >
              <div className="tw-p-[18px] tw-flex tw-flex-col tw-gap-[18px]">
                <div>
                  <div className="tw-text-[15px] tw-font-medium tw-text-[#272522] tw-mb-[15px]">
                    How should variations selector appear to customers?
                  </div>
                  <Field
                    name={FORM_KEYS.DISPLAY_STYLE}
                    component={ExlyRadioGroup}
                    options={DISPLAY_STYLE_OPTIONS}
                    className={{ formLabelClassName: "tw-mb-[9px]" }}
                  />
                </div>
                <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
                  <div>
                    <div className="tw-text-[15px] tw-font-medium tw-text-[#272522]">
                      Preselect default variation
                    </div>
                    <div className="tw-text-sm tw-text-[#27252299] tw-mt-1">
                      When enabled, the first variation is selected for
                      customers by default.
                    </div>
                  </div>
                  <Switch
                    noIcon
                    size="large"
                    color="success"
                    checked={Boolean(values?.[FORM_KEYS.PRESELECT_DEFAULT])}
                    onChange={() =>
                      form.change(
                        FORM_KEYS.PRESELECT_DEFAULT,
                        !values?.[FORM_KEYS.PRESELECT_DEFAULT]
                      )
                    }
                  />
                </div>
              </div>
            </ExlyModal>
          )}
        </Form>
      )}
      {showCreateButton && (
        <Button disabled={createDisabled} onClick={onCreate}>
          {createLabel}
        </Button>
      )}
    </div>
  );
};
